// Authentication context provider
import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthChange } from '../services/authService';
import {
  isSuperuserEmail,
  checkIsSuperuser,
  registerSuperuser,
  updateSuperuserLastLogin,
  saveAuthEmail
} from '../services/superuserService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [authUid, setAuthUid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSuperuser, setIsSuperuser] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthChange(async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        setAuthUid(currentUser.uid);

        // Auto-save email to authEmails collection (so SuperAdmin can see it)
        if (currentUser.email) {
          try {
            await saveAuthEmail(currentUser.uid, currentUser.email);
          } catch (err) {
            console.error('Failed to save auth email:', err);
          }
        }

        // Check superuser status
        try {
          console.log('Checking superuser for email:', currentUser.email);
          console.log('Is superuser email?', isSuperuserEmail(currentUser.email));

          // First check if email is in hardcoded list
          if (currentUser.email && isSuperuserEmail(currentUser.email)) {
            console.log('Email matches superuser list, registering...');
            // Register/update superuser in Firestore
            await registerSuperuser(
              currentUser.uid,
              currentUser.email,
              currentUser.displayName
            );
            console.log('Superuser registered successfully');
            setIsSuperuser(true);
          } else {
            // Check if already registered as superuser in Firestore
            console.log('Email not in list, checking Firestore...');
            const superuserStatus = await checkIsSuperuser(currentUser.uid);
            console.log('Firestore superuser status:', superuserStatus);
            if (superuserStatus) {
              await updateSuperuserLastLogin(currentUser.uid);
            }
            setIsSuperuser(superuserStatus);
          }
        } catch (error) {
          console.error('Error checking superuser status:', error);
          setIsSuperuser(false);
        }
      } else {
        // No authenticated user - don't auto-sign in anonymously
        // User must sign in with Google or Email
        setUser(null);
        setAuthUid(null);
        setIsSuperuser(false);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const value = {
    user,
    authUid,
    loading,
    isSuperuser
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
